import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { WifiOff, Loader2 } from "lucide-react";
import api from "@/lib/api";

const POLL_MS = 2000;

function getClientId() {
  const key = "kindler_follow_client";
  let id = localStorage.getItem(key);
  if (!id) {
    id = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem(key, id);
  }
  return id;
}

export default function FollowSession() {
  const { token } = useParams();
  const [state, setState] = useState(null);
  const [error, setError] = useState(null);
  const timer = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const client = getClientId();
    const poll = async () => {
      try {
        const { data } = await api.get(`/live/${token}`, { params: { client } });
        if (!cancelled) {
          setState(data);
          setError(null);
        }
      } catch (e) {
        if (!cancelled) setError(e?.response?.status === 404 ? "notfound" : "offline");
      }
      if (!cancelled) timer.current = setTimeout(poll, POLL_MS);
    };
    poll();
    return () => {
      cancelled = true;
      clearTimeout(timer.current);
    };
  }, [token]);

  if (error === "notfound") {
    return <Message title="This follow link is no longer active" note="Ask your prayer leader for a fresh link." />;
  }

  if (!state) {
    return (
      <Shell>
        <Loader2 className="h-6 w-6 animate-spin text-white/40" data-testid="follow-loading" />
      </Shell>
    );
  }

  if (state.status === "ended") {
    return <Message title="The prayer session has ended" note={state.session_title} testId="follow-ended" />;
  }

  if (state.blanked) {
    return <Message title="" note="A moment of silence" testId="follow-blanked" />;
  }

  const slide = state.slide;

  return (
    <Shell>
      <div className="flex-1 w-full flex flex-col justify-center px-6 py-10 landscape:px-12" data-testid="follow-slide">
        {!slide || slide.lines.length === 0 ? (
          <p
            className="text-white text-center leading-snug"
            style={{ fontFamily: state.font, fontSize: "clamp(1.4rem, 6vw, 2.6rem)" }}
            data-testid="follow-slide-title"
          >
            {slide?.title || state.session_title}
          </p>
        ) : (
          <div className="space-y-[0.4em]">
            {slide.lines.map((line, i) =>
              line.text === "" ? (
                <div key={i} className="h-[0.5em]" />
              ) : (
                <p
                  key={i}
                  className="text-white leading-snug"
                  style={{
                    fontFamily: state.font,
                    fontSize: line.label ? "clamp(1rem, 4vw, 1.6rem)" : "clamp(1.15rem, 5vw, 2.1rem)",
                  }}
                >
                  {line.text}
                </p>
              )
            )}
          </div>
        )}
      </div>

      <div className="w-full px-6 pb-5 flex items-center justify-between text-[11px] font-mono uppercase tracking-widest text-white/35">
        <span className="truncate pr-3" data-testid="follow-footer-section">
          {[slide?.category, slide?.title].filter(Boolean).join(" · ")}
        </span>
        <span className="shrink-0 flex items-center gap-2">
          {state.status === "reconnecting" && (
            <span className="flex items-center gap-1 text-amber-300/70" data-testid="follow-reconnecting">
              <WifiOff className="h-3 w-3" /> paused
            </span>
          )}
          <span data-testid="follow-page-indicator">
            {state.slide_index + 1} / {state.slide_total}
          </span>
        </span>
      </div>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-black flex flex-col items-center justify-center select-none" data-testid="follow-screen">
      {children}
    </div>
  );
}

function Message({ title, note, testId }) {
  return (
    <Shell>
      <div className="text-center px-8" data-testid={testId}>
        {title && <p className="text-white/80 text-xl font-display">{title}</p>}
        {note && <p className="text-white/40 text-sm mt-2 font-mono uppercase tracking-widest">{note}</p>}
      </div>
    </Shell>
  );
}
